import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup} from '@angular/forms';
import {ToastService} from '@sms-ir/ui-components';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  from,
  map,
  of,
  type Observable,
  Subject,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import {LayoutService} from '@app/core/services';
import {
  COMING_SOON_MESSAGE,
  CONTACTS_PAGE_SIZE,
  SEARCH_DEBOUNCE_MS,
} from '../constants/contacts.constants';
import {EVENT_MEMBERS_API} from '../services/event-members-api.port';
import {EventMetadataMockStore} from '../services/event-metadata.mock.store';
import {USER_EVENTS_API} from '../services/user-events-api.port';
import {showContactsToast} from '../utils/contacts.helpers';
import type {
  Contact,
  ContactEvent,
  CreateContactEventPayload,
  UpdateContactEventPayload,
} from '../types/contacts.types';
import type {CreateEventModalData} from '../components/create-event-modal/create-event-modal.types';

type EventsMobileView = 'events' | 'members';

@Injectable()
export class EventsFacade {
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventsApi = inject(USER_EVENTS_API);
  private readonly membersApi = inject(EVENT_MEMBERS_API);
  private readonly metadataStore = inject(EventMetadataMockStore);
  private readonly toast = inject(ToastService);
  private readonly layout = inject(LayoutService);

  private readonly contactSearch$ = new Subject<string>();
  private initialized = false;

  readonly events = signal<ContactEvent[]>([]);
  readonly eventsLoading = signal(false);
  readonly eventSearchQuery = signal('');
  readonly activeEventId = signal<string | null>(null);
  readonly activeEventName = signal<string | null>(null);

  readonly contacts = signal<Contact[]>([]);
  readonly contactsLoading = signal(false);
  readonly contactSearchQuery = signal('');
  readonly page = signal(1);
  readonly pageSize = signal(CONTACTS_PAGE_SIZE);
  readonly totalCount = signal(0);

  readonly selectionForm = new FormGroup({
    selectedItems: new FormControl<Contact[]>([], {nonNullable: true}),
  });

  readonly selectedItems = toSignal(this.selectionForm.controls.selectedItems.valueChanges, {
    initialValue: [] as Contact[],
  });
  readonly selectedCount = computed(() => this.selectedItems().length);
  readonly hasSelection = computed(() => this.selectedCount() > 0);
  readonly hasSearch = computed(() => this.contactSearchQuery().trim().length > 0);
  readonly hasActiveEvent = computed(() => this.activeEventId() != null);
  readonly showTable = computed(
    () => this.hasActiveEvent() && !this.contactsLoading() && this.totalCount() > 0,
  );
  readonly showEmpty = computed(
    () => this.hasActiveEvent() && !this.contactsLoading() && this.totalCount() === 0,
  );
  readonly showNoEvents = computed(() => !this.eventsLoading() && this.events().length === 0);
  readonly showSelectEventPrompt = computed(
    () => !this.eventsLoading() && this.events().length > 0 && !this.hasActiveEvent(),
  );

  readonly filteredEvents = computed(() => {
    const phrase = this.eventSearchQuery().trim().toLowerCase();
    const items = this.events();
    if (!phrase) return items;
    return items.filter(event => event.name.toLowerCase().includes(phrase));
  });

  readonly mobileView = signal<EventsMobileView>(this.layout.isMobile() ? 'events' : 'members');
  readonly showMobileEventList = computed(
    () => this.layout.isMobile() && this.mobileView() === 'events',
  );
  readonly showMobileMembersDetail = computed(
    () => this.layout.isMobile() && this.mobileView() === 'members',
  );

  readonly activeEvent = computed(() => {
    const id = this.activeEventId();
    if (!id) return null;
    return this.events().find(event => event.id === id) ?? null;
  });

  resolveEventEditData(): CreateEventModalData | null {
    const event = this.activeEvent();
    if (!event) return null;

    const metadata = this.metadataStore.getMetadata(event.id);
    return {
      mode: 'edit',
      name: event.name,
      type: metadata?.type ?? event.type,
      eventDate: metadata?.eventDate ?? event.eventDate ?? null,
    };
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.contactSearch$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(() => {
          this.page.set(1);
          this.clearSelection();
          return this.loadContacts();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.loadEvents().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onEventSearch(value: string): void {
    this.eventSearchQuery.set(value);
  }

  onContactSearch(value: string): void {
    this.contactSearchQuery.set(value);
    this.contactSearch$.next(value.trim());
  }

  selectEvent(eventId: string, eventName?: string): void {
    this.activeEventId.set(eventId);
    this.activeEventName.set(eventName ?? this.events().find(e => e.id === eventId)?.name ?? null);
    this.page.set(1);
    this.clearSelection();
    if (this.layout.isMobile()) {
      this.mobileView.set('members');
    }
    this.reloadContacts();
  }

  onMobileEventBack(): void {
    this.mobileView.set('events');
    this.activeEventId.set(null);
    this.activeEventName.set(null);
    this.contacts.set([]);
    this.totalCount.set(0);
    this.clearSelection();
  }

  onPageChange(nextPage: number): void {
    this.page.set(nextPage);
    this.clearSelection();
    this.reloadContacts();
  }

  clearSelection(): void {
    this.selectionForm.controls.selectedItems.setValue([]);
  }

  ensureEventsLoaded(): Observable<ContactEvent[]> {
    return this.loadEvents().pipe(map(() => this.events()));
  }

  clearSearch(): void {
    this.contactSearchQuery.set('');
    this.contactSearch$.next('');
  }

  createEvent(payload: CreateContactEventPayload): void {
    const metadata = {
      type: payload.type,
      eventDate: payload.type === 'shared' ? (payload.eventDate ?? null) : null,
    };

    this.eventsApi
      .createEvent(payload.name)
      .pipe(
        catchError(() => {
          this.notify('error', 'ایجاد رویداد با خطا مواجه شد.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(created => {
        if (!created) return;

        const event: ContactEvent = {
          ...created,
          type: metadata.type,
          eventDate: metadata.eventDate,
        };

        this.metadataStore.saveMetadata(event.id, metadata, payload.name);
        this.events.update(items => [event, ...items.filter(item => item.id !== event.id)]);
        this.notify('success', 'رویداد با موفقیت ایجاد شد.');
        this.selectEvent(event.id, payload.name);
      });
  }

  updateEvent(payload: UpdateContactEventPayload): void {
    this.eventsApi
      .updateEvent(payload.id, payload.name)
      .pipe(
        catchError(() => {
          this.notify('error', 'ویرایش رویداد با خطا مواجه شد.');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.metadataStore.saveMetadata(
          payload.id,
          {
            type: payload.type,
            eventDate: payload.type === 'shared' ? (payload.eventDate ?? null) : null,
          },
          payload.name,
        );
        this.notify('success', 'تغییرات رویداد ذخیره شد.');
        this.activeEventName.set(payload.name);
        this.reloadEvents();
      });
  }

  deleteActiveEvent(): void {
    const eventId = this.activeEventId();
    if (!eventId) return;

    this.eventsApi
      .deleteEvent(eventId)
      .pipe(
        catchError(() => {
          this.notify('error', 'حذف رویداد با خطا مواجه شد.');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.metadataStore.deleteMetadata(eventId);
        this.notify('success', 'رویداد مورد نظر حذف شد.');
        this.activeEventId.set(null);
        this.activeEventName.set(null);
        this.contacts.set([]);
        this.totalCount.set(0);
        this.clearSelection();
        if (this.layout.isMobile()) {
          this.mobileView.set('events');
        }
        this.reloadEvents();
      });
  }

  removeContact(eventId: string, contactId: string, deleteGlobally: boolean): void {
    this.membersApi
      .removeContactFromEvent(eventId, contactId, deleteGlobally)
      .pipe(
        catchError(() => {
          this.notify('error', 'حذف مخاطب با خطا مواجه شد.');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.notify('success', 'مخاطب با موفقیت حذف شد.');
        this.clearSelection();
        this.refreshLists();
      });
  }

  removeSelectedContacts(deleteGlobally: boolean): void {
    const items = this.selectedItems();
    const eventId = this.activeEventId();
    if (!items.length || !eventId) return;

    from(items)
      .pipe(
        concatMap(contact =>
          this.membersApi
            .removeContactFromEvent(eventId, contact.id, deleteGlobally)
            .pipe(catchError(() => of(undefined))),
        ),
        toArray(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.notify('success', `${items.length.toLocaleString('fa-IR')} مخاطب با موفقیت حذف شد.`);
        this.clearSelection();
        this.refreshLists();
      });
  }

  refreshContacts(): void {
    this.refreshLists();
  }

  exportEventMember(_contact: Contact): void {
    this.notify('success', COMING_SOON_MESSAGE);
  }

  updateMemberEventDate(contact: Contact, eventDate: string): void {
    const eventId = this.activeEventId();
    if (!eventId || !contact.id || !eventDate) return;

    this.membersApi
      .updateMemberEventDate(eventId, contact.id, eventDate)
      .pipe(
        catchError(() => {
          this.notify('error', 'ثبت تاریخ رویداد با خطا مواجه شد.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        if (result === null) return;
        this.notify('success', 'تاریخ رویداد با موفقیت ذخیره شد.');
        this.reloadContacts();
      });
  }

  handleAddContact(): void {
    this.notify('success', COMING_SOON_MESSAGE);
  }

  handleFilterAction(): void {
    this.notify('success', COMING_SOON_MESSAGE);
  }

  handleBulkAction(action: 'moveToGroup' | 'addToEvent' | 'delete' | 'export'): void {
    if (action === 'delete') return;
    this.notify('success', COMING_SOON_MESSAGE);
  }

  private refreshLists(): void {
    forkJoin([this.loadEvents(), this.loadContacts()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private reloadEvents(onLoaded?: () => void): void {
    this.loadEvents().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => onLoaded?.());
  }

  private reloadContacts(): void {
    this.loadContacts().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private loadEvents() {
    this.eventsLoading.set(true);
    return this.eventsApi.listEvents(true).pipe(
      catchError(() => of([] as ContactEvent[])),
      finalize(() => this.eventsLoading.set(false)),
      tap(rawEvents => {
        const enriched = this.metadataStore.enrichEvents(rawEvents);
        this.membersApi.seedMembersForEvents(enriched);
        this.events.set(
          enriched.map(event => ({
            ...event,
            usedCount:
              event.usedCount && event.usedCount > 0
                ? event.usedCount
                : this.membersApi.getMemberCount(event.id),
          })),
        );

        const activeId = this.activeEventId();
        if (activeId) {
          const stillExists = enriched.some(event => event.id === activeId);
          if (!stillExists) {
            this.activeEventId.set(null);
            this.activeEventName.set(null);
            this.contacts.set([]);
            this.totalCount.set(0);
          } else {
            const active = enriched.find(event => event.id === activeId);
            if (active) {
              this.activeEventName.set(active.name);
            }
          }
        }
      }),
    );
  }

  private loadContacts() {
    const eventId = this.activeEventId();
    if (!eventId) {
      this.contacts.set([]);
      this.totalCount.set(0);
      return of({items: [], total: 0});
    }

    this.contactsLoading.set(true);
    return this.membersApi
      .listContactsInEvent({
        eventId,
        phrase: this.contactSearchQuery().trim() || undefined,
        page: this.page(),
        pageSize: this.pageSize(),
      })
      .pipe(
        catchError(() => of({items: [], total: 0})),
        finalize(() => this.contactsLoading.set(false)),
        tap(result => {
          this.contacts.set(result.items);
          this.totalCount.set(result.total);
        }),
      );
  }

  private notify(variant: 'error' | 'success' | 'warning', message: string): void {
    showContactsToast(this.toast, variant, message);
  }
}
